import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Tabs,
  Tab,
  Typography,
  Button,
  Stack,
  IconButton,
  CircularProgress,
  Modal,
  Slide,
  Alert,
} from "@mui/material";
import { FormProvider, UseFormReturn, useWatch } from "react-hook-form";
import { GridColDef, GridSelectionModel } from "@mui/x-data-grid";
import { MedintDataContext } from "../../../../../Context/MedintContextWrapper";
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFDateTimePicker,
  RHFTextField,
} from "../../../../../../RHFComponents/RHFComponents";
import { StyledDataGrid } from "../../../../../../AppointmentManagement/AppointmentManagementDetail/AppointmentManagementDetail";
import { ReactComponent as ModifyIcon } from "../../../../../../../assets/icons/MedintModifyIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../../../../assets/icons/MedintDeleteIcon.svg";
import { ReactComponent as AddIcon } from "../../../../../../../assets/icons/MedintAddIcon.svg";
import { ReactComponent as CopyFromDoctorIcon } from "../../../../../../../assets/icons/MedintCopyFromDoctorIcon.svg";
import {
  pgmedd,
  pgmedg,
  pgmedo,
  pgmedp,
  pgmedt,
  pgmedtage,
  pgmedv,
} from "../../../../../../../networking";
import dayjs from "dayjs";
import MedintFormInterface from "../../../../../../../services/medintInterfaces";
import { formatHour } from "../../../../../../AppointmentResearch/AppointmentResearch";
import { fmtDate } from "../../../MedintBox/MedintBox";
import TipologiesModal from "./MedintAgendaManagement/TipologiesModal";
import GiornateModal from "./MedintAgendaManagement/GiornateModal";
import OrariFissiModal from "./MedintAgendaManagement/OrariFissiModal";
import PrestazioniAbilitateModal from "./MedintAgendaManagement/PrestazioniAbilitateModal";
import { Helmet } from "react-helmet-async";
import PeriodiValiditaModal from "./MedintAgendaManagement/PeriodiValiditaModal";
import {
  Action,
  AgendaApi,
  Entity,
  ModalState,
  TabKey,
} from "../../../../../../../services/medintAgendaFormTypes";
import SospensioniAgendeModal from "./MedintAgendaManagement/SospensioniAgendeModal";
import AttrGPModal from "./MedintAgendaManagement/AttrGPModal";

// L’API spesso ritorna booleani come stringhe
// Converte "1"/1 in true, tutto il resto false
// Utile per flag tipo FLAGALTRIORARI che l'api manda come "0"/"1"
const toBool01 = (v?: string | number | null) => String(v ?? "") === "1";

// Converte "S"/"N" (case-insensitive) in true/false
// Utile per flag tipo FLAGULTIMOAPP
const toBoolSN = (v?: string | null) => String(v ?? "").toUpperCase() === "S";

// Normalizza numeri che arrivano come stringhe
// In molte api 0 arriva come "0.00": qui lo trasformiamo in "0" per UI più pulita
const safeNumStr = (v?: string | null) => (v && v !== "0.00" ? v : "0");

// Funzione helper per formattare le fasce orarie in modo coerente, gestendo i casi di orario mancante o non valido
// Se non ho né ora inizio né ora fine, ritorno stringa vuota
// Se ho entrambe, ritorno "oraInizio - oraFine"
// Se ho solo un'ora, ritorno "ora"
// Questo mi permette di mostrare le fasce orarie in modo chiaro e consistente, anche quando i dati non sono completi
const fascia = (da?: string, a?: string) => {
  const fDa = da && da !== "0" ? formatHour(da) : "";
  const fA = a && a !== "0" ? formatHour(a) : "";
  if (!fDa && !fA) return "";
  if (fDa && fA) return `${fDa} - ${fA}`;
  return fDa || fA;
};

export function AllertMessage(props: {
  showAllert: boolean;
  dataToSend?: any;
  showSummary?: Function;
  setShowAllert: (value: boolean) => void;
  submitFunction?: ((value: any) => void) | null;
  message: string;
  severity: any;
}) {
  return (
    <Modal open={props.showAllert}>
      <Slide in={props.showAllert} direction="down" timeout={800}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "50vh",
            "& .MuiPaper-root": {
              paddingRight: 5,
              paddingLeft: 5,
            },
          }}
        >
          <Alert
            severity={props.severity || "error"}
            action={
              props.submitFunction && props.severity === "warning" ? (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="containedError"
                    size="small"
                    onClick={() => {
                      props.setShowAllert(false);
                    }}
                  >
                    Uscita
                  </Button>
                  <Button
                    variant="containedCustom"
                    size="small"
                    onClick={() => {
                      if (props.submitFunction) {
                        props.submitFunction(props?.dataToSend);
                      }
                    }}
                  >
                    Conferma
                  </Button>
                </Box>
              ) : props.submitFunction &&
                props.showSummary &&
                props.severity === "success" ? (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="containedCustom"
                    size="small"
                    onClick={() => {
                      if (props.showSummary) {
                        props.showSummary();
                      }
                    }}
                  >
                    Dettaglio
                  </Button>
                </Box>
              ) : (
                ""
              )
            }
            sx={{
              maxWidth: "150vh",
              width: "90%",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "center",
              justifyItems: "center",
              transform: "translate3d(20%, 100%, 0%)",
              flexDirection: "row",
              "& .MuiAlert-icon": {
                fontSize: 30,
              },
            }}
          >
            <Typography
              component="div"
              marginTop={"20px"}
              marginLeft={"20px"}
              marginRight={"10px"}
              marginBottom={"20px"}
              fontSize={"20px"}
              fontStyle={"capitalize"}
            >
              {props.message}
            </Typography>
          </Alert>
        </Box>
      </Slide>
    </Modal>
  );
}

const MedintAgendaFormDetail = (props: {
  fetchDataMedico: () => Promise<void>;
  onClose?: () => void;
  formMethods: UseFormReturn<MedintFormInterface>;
}) => {
  const context = useContext(MedintDataContext);

  const watch_wholeForm = props.formMethods.getValues("medIntNuovoProgramma");
  const watch_state = props?.formMethods?.watch("medIntNuovoProgramma.state");
  const watch_chiave = props?.formMethods?.watch("medIntNuovoProgramma.chiave");

  const [tab, setTab] = useState<TabKey>("dettaglio");

  const [loading, setLoading] = useState(false);

  //Stato che tiene traccia se l'utente ha salvato il nuovo record creato
  //Se true, il record è finalizzato e di conseguenza all'uscita dal form non verrà cancellato
  const [isFinalizedRecord, setIsFinalizedRecord] = useState<boolean>(false);

  // Evita doppie create in insert come ad esempio render multipli
  const didCreateRef = useRef(false);

  // Evita doppie read sullo stesso id
  const lastLoadedIdRef = useRef<string | null>(null);

  // Evita chiamate sovrapposte come doppi click
  // Evita chiamate sovrapposte di LETTURA (pgmedtage)
  const inFlightReadRef = useRef(false);

  // Evita doppio click / rientri sulla CREATE (pgmedt)
  const inFlightCreateRef = useRef(false);

  // Stati per la gestione della selezione nella UI,
  // che mi permettono di identificare quale tipologia, giornata o orario è attualmente selezionato
  const [selectedTipologiaId, setSelectedTipologiaId] = useState<string | null>(
    null,
  );
  const [selectedGiornataId, setSelectedGiornataId] = useState<string | null>(
    null,
  );
  const [selectedOrarioId, setSelectedOrarioId] = useState<string | null>(null);

  //Stato booleano che disabilita campi e bottoni nel caso ci troviamo nella modalità copy
  const [isCopyMode, setIsCopyMode] = useState<boolean>(watch_state === "copy");

  // Stato per salvare l'id del nuovo record di pgmedt creato in insert,
  // in modo da poterlo recuperare e cancellare se esco senza salvare
  const [createdIdPgmedt, setCreatedIdPgmedt] = useState<string>("");

  //Stato per la gestione del modale
  const [modal, setModal] = useState<ModalState>({ open: false });

  //Stati per la gestione del componente allert
  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  //Tipo per la gestione della cancellazione
  type PendingDelete = { entity: Entity; raw: any } | null;

  //Stato per la gestione della cancellazione
  const [pendingDelete, setPendingDelete] = useState<PendingDelete>(null);

  // Funzione che apre il modale corrispondente in base a Entity e definisce l'azione da eseguire (create, update, delete)
  // attraverso Action
  const openModal = (
    entity: Entity,
    action: Action = "insert",
    payload?: any,
  ) => setModal({ open: true, entity, action, payload });

  //Funzione che chiude il modale
  const closeModal = () => setModal({ open: false });

  //Funzione di chiusura del form, che resetta gli stati e i ref per evitare problemi alla riapertura
  const handleClose = async () => {
    // Reset dei guard e degli stati di caricamento:
    // serve a evitare che, riaprendo il form, saltino le chiamate perché i ref risultano ancora sporchi
    didCreateRef.current = false;
    lastLoadedIdRef.current = null;
    inFlightReadRef.current = false;
    inFlightCreateRef.current = false;
    setCreatedIdPgmedt("");

    if (watch_state === "insert" && createdIdPgmedt && !isFinalizedRecord) {
      setLoading(true);
      try {
        // In insert creiamo un record per ottenere la chiave (id)
        // Se l'utente esce senza confermare, lo cancelliamo
        await pgmedt({ crud: "D", prgint: createdIdPgmedt });
      } catch (error) {
        console.error("Errore cancellazione agenda non salvata:", error);
      } finally {
        setLoading(false);
      }
    }

    setCreatedIdPgmedt("");
    setIsFinalizedRecord(false);
    context?.setFullViewAgendaForm(false);
    props?.fetchDataMedico();
  };

  const getPgmedtageData = async (
    idPgmedt: any,
    opts?: { force?: boolean },
  ) => {
    if (!idPgmedt) return;

    // Se ho già caricato il pgmedt con questo id, non faccio nulla
    // Per ricaricare il pgmedt, devo forzare la chiamata con force
    if (!opts?.force && lastLoadedIdRef.current === idPgmedt) {
      return;
    }
    // Se ho una chiamata in corsa, non faccio nulla
    if (inFlightReadRef.current) {
      return;
    }

    setLoading(true);
    inFlightReadRef.current = true;

    try {
      const response = await pgmedtage({
        crud: "R",
        codicePgmedt: "",
        idPgmedt,
      });

      const dataPgmedtage = response?.data?.dati?.[0] as AgendaApi;

      if (!dataPgmedtage) {
        console.warn("[WARN] pgmedtage: nessun dato per", idPgmedt);
        return;
      }

      // Segno lastLoadedId solo quando ho dei dati validi
      lastLoadedIdRef.current = idPgmedt;

      props.formMethods.reset(
        {
          ...props.formMethods.getValues(),
          medIntNuovoProgramma: {
            ...props.formMethods.getValues("medIntNuovoProgramma"),
            codProg: dataPgmedtage.CODICEAGENDA ?? "",
            descrCompl: dataPgmedtage.DESCRAGENDA ?? "",
            descrRid: dataPgmedtage.DESCRRIDAGENDA ?? "",
            dataIni: dataPgmedtage.DATAINIZIOVALIDITA ?? null,
            dataFin: dataPgmedtage.DATAFINEVALIDITA ?? null,
            appUltFascOra: toBoolSN(dataPgmedtage.FLAGULTIMOAPP),
            gpPerm: toBool01(dataPgmedtage.FLAGALTRIORARI),
            disciplinaQD: dataPgmedtage.DISCIPLINAQD ?? null,
            disciplinaAmm: dataPgmedtage.DISCIPLINAAMM ?? null,
            tipologie: dataPgmedtage.tipoPrenotazione ?? [],
            periodiVal: dataPgmedtage.periodiValidita ?? [],
            opLogicoQd: dataPgmedtage.OPLOGICOQD ?? "",
            codDisciplinaQd: dataPgmedtage.CODDISCIPLINAQD ?? "",
            codDisciplinaAmm: dataPgmedtage.CODDISCIPLINAMM ?? "",
            idDisciplinaQd: dataPgmedtage.IDDISCIPLINAQD ?? "",
            idDisciplinaAmm: dataPgmedtage.IDDISCIPLINAAMM ?? "",
            note: {
              lunedi: dataPgmedtage.NOTELUN ?? "",
              martedi: dataPgmedtage.NOTEMAR ?? "",
              mercoledi: dataPgmedtage.NOTEMER ?? "",
              giovedi: dataPgmedtage.NOTEGIO ?? "",
              venerdi: dataPgmedtage.NOTEVEN ?? "",
              sabato: dataPgmedtage.NOTESAB ?? "",
              domenica: dataPgmedtage.NOTEDOM ?? "",
              standard: dataPgmedtage.NOTEGEN ?? "",
            },
          },
        },
        { keepDirtyValues: true, keepErrors: true, keepTouched: true },
      );
    } catch (error: any) {
      console.error("Errore recupero dati pgmedtage:", error);
    } finally {
      inFlightReadRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    //Se non ho l'idPgmedt (watch_chiave) e sono in insert
    //creo un nuovo record di pgmedt per avere un id da cui recuperare i dati dell'agenda
    if (!watch_chiave && watch_state === "insert") {
      //Funzione per la creazione di un nuovo record di pgmedt
      const createPgmedtData = async () => {
        // inFlightCreateRef:
        // evita doppie CREATE in caso di trigger multipli ( rerender / doppio click)
        if (inFlightCreateRef.current) {
          console.log("[SKIP] create pgmedt in flight");
          return;
        }

        inFlightCreateRef.current = true;

        try {
          const responseCreatePgmedt = await pgmedt({
            aziendaPrenotazione: "MEDITEL",
            crud: "c",
            dati: {
              pgmedt: {
                PGMEDT_CODICE: "",
                DATA_INI_VAL: props.formMethods.getValues(
                  "medIntNuovoProgramma.dataIni",
                )
                  ? dayjs(
                      props.formMethods.getValues(
                        "medIntNuovoProgramma.dataIni",
                      ),
                    ).format("YYYY-MM-DD")
                  : "",
                DATA_FINE_VAL: props.formMethods.getValues(
                  "medIntNuovoProgramma.dataFin",
                )
                  ? dayjs(
                      props.formMethods.getValues(
                        "medIntNuovoProgramma.dataFin",
                      ),
                    ).format("YYYY-MM-DD")
                  : "",
                PGMEDT_DESCR: "",
                PGMEDT_DESC_RIDOT: "",
                PGMEDT_AP_ULT_ORA: "N",
                PGMEDT_DES_LUN: "",
                PGMEDT_DES_MAR: "",
                PGMEDT_DES_MER: "",
                PGMEDT_DES_GIO: "",
                PGMEDT_DES_VEN: "",
                PGMEDT_DES_SAB: "",
                PGMEDT_DES_DOM: "",
                PGMEDT_DES_GEN: "",
                NMNSPE_CODICE: "",
                PGMEDT_NORMALIZZAZIONE: "",
                PGMEDT_OP_LOGICO_QD: "",
                PGMEDT_SW_RAGGRUPPABILE: "",
                NMNSPE_AMM_CODICE: "",
                PGMEDT_SW_ALTRI_ORARI: "",
                PGMEDT_SW_SOLO_GP: "0",
                PGMEDT_SW_AGGIUNTIVA: "",
              },
            },
          });
          console.log("dati pgmedt creati:", responseCreatePgmedt);
          // Salvo l'id del nuovo record di pgmedt per poter recuperare i dati dell'agenda e per cancellarlo
          // in caso di uscita senza salvataggio
          setCreatedIdPgmedt(responseCreatePgmedt?.data?.idNuovoRecord ?? "");
          //Se la creazione è andata a buon fine e ho l'id del nuovo record, recupero i dati dell'agenda con quell'id
          if (responseCreatePgmedt.data.idNuovoRecord) {
            getPgmedtageData(responseCreatePgmedt.data.idNuovoRecord, {
              force: true,
            });
          }
        } catch (error: any) {
          console.error("Errore creazione pgmedt in insert:", error);
          // didCreateRef non è usato nel flusso: lo lascio per compatibilità,
          // ma qui non influisce sulla create (il guard reale è inFlightCreateRef)
          didCreateRef.current = false;
        } finally {
          // Importante: rilascio lock create
          inFlightCreateRef.current = false;
        }
      };
      createPgmedtData();
    } else {
      //Se ho l'idPgmedt, recupero i dati dell'agenda
      getPgmedtageData(watch_chiave);
    }
  }, [watch_chiave, watch_state]);

  //Effetto che tiene traccia se l'utente sta copiando un record
  useEffect(() => {
    setIsCopyMode(watch_state === "copy");
  }, [watch_state]);

  //Effetto per gestire il tempo di visualizzazione dell'alert
  useEffect(() => {
    if (!showAllert) return;

    // warning resta aperto finché l’utente decide
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  // Funzione per cancellare un record da una tabella
  const handleDelete = (entity: Entity, raw: any) => {
    if (isCopyMode) return;

    // setto il record da cancellare
    setPendingDelete({ entity, raw });

    // Apro AllertMessage in warning
    setSeverity("warning");
    setMessage("Confermi l'eliminazione?");
    setShowAllert(true);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;

    const { entity, raw } = pendingDelete;

    setLoading(true);

    try {
      if (entity === "tipologia") {
        await pgmedd?.({ crud: "d", prgint: String(raw?.IDPGMEDD ?? "") });

        if (String(raw?.IDPGMEDD) === String(selectedTipologiaId)) {
          setSelectedTipologiaId(null);
          setSelectedGiornataId(null);
          setSelectedOrarioId(null);
        }
      }

      if (entity === "giornata") {
        await pgmedg({
          crud: "d",
          prgint: Number(raw?.IDPGMEDG ?? raw?.PGMEDG_PRGINT),
        });

        if (String(raw?.IDPGMEDG) === String(selectedGiornataId)) {
          setSelectedGiornataId(null);
          setSelectedOrarioId(null);
        }
      }

      if (entity === "orario") {
        await pgmedo?.({ crud: "d", prgint: Number(raw?.IDPGMEDO ?? "") });

        if (String(raw?.IDPGMEDO) === String(selectedOrarioId)) {
          setSelectedOrarioId(null);
        }
      }

      if (entity === "prestazione") {
        await pgmedp?.({ crud: "d", prgint: Number(raw?.IDPGMEDP ?? "") });
      }

      if (entity === "periodo") {
        await pgmedv?.({ crud: "d", prgint: Number(raw?.IDPGMEDV ?? "") });
      }

      // Refresho i dati
      lastLoadedIdRef.current = null;
      await getPgmedtageData(watch_chiave, { force: true });

      //Restituisco l'esito
      setSeverity("success");
      setMessage("Eliminazione completata con successo.");
    } catch (e: any) {
      console.error("Errore cancellazione:", e);
      setSeverity("error");
      setMessage("Errore durante l'eliminazione.");
    } finally {
      setLoading(false);
      setPendingDelete(null);
    }
  };

  // Questi useMemo vengono utilizzati per ottenere i dati da mostrare nel form attraverso lo stato dell'agenda,
  // in modo da avere una fonte di verità unica (l'oggetto agenda) da cui derivano tutte le informazioni visualizzate
  // e modificate nel form, evitando di dover gestire stati separati
  // per ogni livello di dati (tipologie, giornate, orari, prestazioni)
  const tipologie = useWatch({
    control: props.formMethods.control,
    name: "medIntNuovoProgramma.tipologie",
  });

  const periodiVal = useWatch({
    control: props.formMethods.control,
    name: "medIntNuovoProgramma.periodiVal",
  });

  const selectedTipologia = useMemo(
    () =>
      (tipologie ?? []).find((t) => t.IDPGMEDD === selectedTipologiaId) ?? null,
    [tipologie, selectedTipologiaId],
  );

  const giornate = useMemo(
    () => selectedTipologia?.orariGiornalieri ?? [],
    [selectedTipologia],
  );

  const selectedGiornata = useMemo(
    () => giornate.find((g: any) => g.IDPGMEDG === selectedGiornataId) ?? null,
    [giornate, selectedGiornataId],
  );

  const orariFissi = useMemo(
    () => selectedGiornata?.orariFissi ?? [],
    [selectedGiornata],
  );

  const selectedOrario = useMemo(
    () => orariFissi.find((o: any) => o.IDPGMEDO === selectedOrarioId) ?? null,
    [orariFissi, selectedOrarioId],
  );

  const prestazioni = useMemo(
    () => selectedOrario?.prestazioniAbilitate ?? [],
    [selectedOrario],
  );

  // Le rows sono dati pronti per la ui
  // quindi li costruisco con useMemo e li passo al datagrid
  //Tipologie: tabella principale
  //salvo anche _raw così quando devo modificare o cancellare ho l’oggetto completo
  const tipologieRows = useMemo(
    () =>
      (tipologie ?? []).map((t) => ({
        id: t.IDPGMEDD,
        tipologia: ` ${t.DESCRTIPOAGENDA ?? ""}`.trim(),
        maxMese: safeNumStr(t.MAXMESE ?? "0"),
        maxSett: safeNumStr(t.MAXSETT ?? "0"),
        maxGiorno: safeNumStr(t.MAXGIO ?? "0"),
        _raw: t, //E' l'oggetto originale completo per le azioni
      })),
    [tipologie],
  );

  //Tabella periodi validità
  //Uso fmtDate per convertire YYYY-MM-DD in DD/MM/YY (non essendo dentro un rhf ma in una tabella va formattato così)
  const periodiValiditaRows = useMemo(
    () =>
      (periodiVal ?? []).map((p) => ({
        id: p.IDPGMEDV,
        dataInizio: fmtDate(p.DATAINIZIOVALIDITA),
        dataFine: fmtDate(p.DATAFINEVALIDITA),
        _raw: p,
      })),
    [periodiVal],
  );

  //Tabella giornate, la seconda, che mostra le giornate in base alla tipologia selezionata
  // Qui formattiamo le 5 fasce orarie in stringhe leggibili, così il grid non deve fare calcoli
  const giornateRows = useMemo(
    () =>
      giornate.map((g: any) => ({
        id: g.IDPGMEDG,
        giornata: g.DESCRGGSETT ?? "",
        nMax: g.MAXESAMI ?? "0",
        dur: g.DURATASTD ?? "0",
        fascia1: fascia(g.ORADA1, g.ORAA1),
        fascia2: fascia(g.ORADA2, g.ORAA2),
        fascia3: fascia(g.ORADA3, g.ORAA3),
        fascia4: fascia(g.ORADA4, g.ORAA4),
        fascia5: fascia(g.ORADA5, g.ORAA5),
        ric: String(g.FLAGRICORRENZA ?? "0"),
        _raw: g,
      })),
    [giornate],
  );

  //Terza tabella orari fissi, che mostra dati in base alla giornata selezionata
  //Viene usata la formatHour per normalizzare i valori come "900" in "09:00"
  const orariFissiRows = useMemo(
    () =>
      orariFissi.map((o: any) => ({
        id: o.IDPGMEDO,
        orario: formatHour(o.ORE ?? ""),
        nMax: o.MAXESAMI ?? "0",
        durata: o.DURATASTD ?? "0",
        _raw: o,
      })),
    [orariFissi],
  );

  //Quarta tabella che dipende dall'orario selezionato e mostra le prestazioni abilitate per quell'orario
  const prestazioniRows = useMemo(
    () =>
      prestazioni.map((p: any) => ({
        id: p.IDPGMEDP,
        specialita: p.CODICESPECIALITA ?? "",
        prestazione: ` ${p.DESCRPRESTAZIONE ?? ""}`.trim(),
        nMax: p.MAXESAMI ?? "0",
        durata: p.DURATASTD ?? "0",
        _raw: p,
      })),
    [prestazioni],
  );

  const onSubmit = async (data: MedintFormInterface) => {
    console.log("FORM submit:", data.medIntNuovoProgramma);
    setLoading(true);
    const prgint = watch_chiave ?? createdIdPgmedt;

    const crud =
      watch_state === "insert" ? "u" : watch_state === "update" ? "u" : "p";

    try {
      const payload = {
        crud: crud,
        prgint: crud === "u" || crud === "p" ? prgint : createdIdPgmedt,
        ...(crud === "u"
          ? {
              dati: {
                pgmedt: {
                  PGMEDT_PRGINT: data?.medIntNuovoProgramma?.chiave ?? "",
                  PGMEDT_CODICE: data?.medIntNuovoProgramma?.codProg ?? "",
                  DATA_INI_VAL: data?.medIntNuovoProgramma?.dataIni ?? "",
                  DATA_FINE_VAL: data?.medIntNuovoProgramma?.dataFin ?? "",
                  PGMEDT_DESCR: data?.medIntNuovoProgramma?.descrCompl ?? "",
                  PGMEDT_DESC_RIDOT: data?.medIntNuovoProgramma?.descrRid ?? "",
                  PGMEDT_AP_ULT_ORA: data?.medIntNuovoProgramma?.appUltFascOra
                    ? "S"
                    : "N",
                  PGMEDT_DES_LUN:
                    data?.medIntNuovoProgramma?.note?.lunedi ?? "",
                  PGMEDT_DES_MAR:
                    data?.medIntNuovoProgramma?.note?.martedi ?? "",
                  PGMEDT_DES_MER:
                    data?.medIntNuovoProgramma?.note?.mercoledi ?? "",
                  PGMEDT_DES_GIO:
                    data?.medIntNuovoProgramma?.note?.giovedi ?? "",
                  PGMEDT_DES_VEN:
                    data?.medIntNuovoProgramma?.note?.venerdi ?? "",
                  PGMEDT_DES_SAB:
                    data?.medIntNuovoProgramma?.note?.sabato ?? "",
                  PGMEDT_DES_DOM:
                    data?.medIntNuovoProgramma?.note?.domenica ?? "",
                  PGMEDT_DES_GEN:
                    data?.medIntNuovoProgramma?.note?.standard ?? "",
                  NMNSPE_CODICE:
                    data?.medIntNuovoProgramma?.codDisciplinaQd ?? "",
                  PGMEDT_NORMALIZZAZIONE: "",
                  PGMEDT_OP_LOGICO_QD: "",
                  PGMEDT_SW_RAGGRUPPABILE: "",
                  NMNSPE_AMM_CODICE:
                    data?.medIntNuovoProgramma?.codDisciplinaAmm ?? "",
                  PGMEDT_SW_ALTRI_ORARI: "",
                  PGMEDT_SW_SOLO_GP: "0",
                  PGMEDT_SW_AGGIUNTIVA: "",
                },
              },
            }
          : { datainival: data?.medIntNuovoProgramma?.dataIni }),
      };
      const response = await pgmedt(payload);

      console.log({ response });

      getPgmedtageData(prgint, { force: true });
      if (response?.data?.esito === "OK") {
        setIsFinalizedRecord(true);
      }
    } catch (error) {
      console.error("Error sending pgmedt:", error);
    } finally {
      setLoading(false);
    }
  };

  // Stili
  const BLUE = "#003267";

  //Stile per la selezione delle righe dello styledatagrid
  const sxGridSelectedRow = {
    "& .MuiDataGrid-row.Mui-selected": {
      backgroundColor: "rgba(0, 50, 103, 0.08) !important",
    },
    "& .MuiDataGrid-row.Mui-selected:hover": {
      backgroundColor: "rgba(0, 50, 103, 0.12) !important",
    },
  };

  const sxTabs = {
    mb: 2,
    minHeight: 38,
    "& .MuiTabs-indicator": {
      backgroundColor: BLUE,
      height: 3,
      borderRadius: 3,
    },
    "& .MuiTab-root": {
      minHeight: 38,
      textTransform: "none",
      px: 0,
      mr: 3,
      fontFamily: "Montserrat",
      fontWeight: "bold",
      color: "#9e9e9e",
      transition: "color .2s ease",
    },
    "& .MuiTab-root.Mui-selected": { color: BLUE },
  } as const;

  const sxNotesCard = {
    border: "1px solid #E0E0E0",
    borderRadius: 1,
    bgcolor: "white",
    p: 2,
  } as const;
  const sxNoteItem = { mb: 2 } as const;
  const sxCounter = { mt: 0.5, fontSize: 11, color: "#777" } as const;

  const sxCard = {
    borderRadius: 1,
    border: "1px solid #E0E0E0",
    bgcolor: "white",
  } as const;
  const sxSection = { ...sxCard, mt: 2, overflow: "hidden" } as const;
  const sxSectionHeader = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
    py: 1,
    bgcolor: "white",
    borderBottom: "none",
  } as const;
  const sxSectionTitle = {
    fontFamily: "Montserrat",
    fontWeight: "bold",
    fontSize: 14,
    color: "var(--text-secondary, rgba(0, 0, 0, 0.60))",
  } as const;

  const styles = useMemo(
    () => ({
      page: { p: 2, bgcolor: "#f4f6f8" },
      shell: {
        p: 2,
        borderRadius: 1.5,
        border: "1px solid #E0E0E0",
        bgcolor: "white",
      },
      tabsWrap: { mb: 1 },
      actionsBar: {
        mt: 2,
        pt: 1.5,
        borderTop: "1px solid #E0E0E0",
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        gap: 1,
      },
    }),
    [],
  );

  const createActionsCol = (opts?: {
    onEdit?: (row: any) => void;
    onCopy?: (row: any) => void;
    onDelete?: (row: any) => void;
    minWidth?: number;
    hideCopy?: boolean;
  }): GridColDef => {
    const {
      onEdit,
      onCopy,
      onDelete,
      minWidth = 110,
      hideCopy = false,
    } = opts ?? {};
    return {
      field: "azioni",
      headerName: "Azioni",
      headerAlign: "center",
      align: "center",
      flex: 0.5,
      minWidth,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <IconButton
            size="small"
            aria-label="Modifica"
            sx={{ color: "#024E88" }}
            onClick={() => onEdit?.(params.row)}
          >
            <ModifyIcon />
          </IconButton>
          {!hideCopy && (
            <IconButton
              size="small"
              onClick={() => onCopy?.(params.row)}
              sx={{ color: "#024E88" }}
            >
              <CopyFromDoctorIcon />
            </IconButton>
          )}
          <IconButton
            size="small"
            aria-label="Elimina"
            sx={{ color: "#d32f2f" }}
            onClick={() => onDelete?.(params.row)}
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    };
  };

  // Colonne griglia
  const tipologieCols: GridColDef[] = [
    { field: "tipologia", headerName: "Tipologia", flex: 1.3, minWidth: 160 },
    {
      field: "maxMese",
      headerName: "Max. Mese",
      flex: 0.7,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "maxSett",
      headerName: "Max. Sett.",
      flex: 0.7,
      minWidth: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "maxGiorno",
      headerName: "Max. Giorno",
      flex: 0.8,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
    },
    createActionsCol({
      onEdit: (row) => openModal("tipologia", "edit", row._raw),
      onCopy: (row) => openModal("tipologia", "copy", row._raw),
      onDelete: (row) => handleDelete("tipologia", row._raw),
    }),
  ];

  const giornateCols: GridColDef[] = [
    { field: "giornata", headerName: "Giornata", flex: 1.1, minWidth: 140 },
    {
      field: "nMax",
      headerName: "N° Max",
      flex: 0.5,
      minWidth: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "dur",
      headerName: "Dur.",
      flex: 0.45,
      minWidth: 70,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fascia1",
      headerName: "Dalle - Alle",
      flex: 0.75,
      minWidth: 115,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fascia2",
      headerName: "Dalle - Alle",
      flex: 0.75,
      minWidth: 115,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fascia3",
      headerName: "Dalle - Alle",
      flex: 0.75,
      minWidth: 115,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fascia4",
      headerName: "Dalle - Alle",
      flex: 0.75,
      minWidth: 115,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "fascia5",
      headerName: "Dalle - Alle",
      flex: 0.75,
      minWidth: 115,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ric",
      headerName: "Ric.",
      flex: 0.45,
      minWidth: 70,
      align: "center",
      headerAlign: "center",
    },
    createActionsCol({
      onEdit: (row) => openModal("giornata", "edit", row._raw),
      onCopy: (row) => openModal("giornata", "copy", row._raw),
      onDelete: (row) => handleDelete("giornata", row._raw),
      minWidth: 100,
    }),
  ];

  const orariFissiCols: GridColDef[] = [
    { field: "orario", headerName: "Orario", flex: 0.9, minWidth: 110 },
    {
      field: "nMax",
      headerName: "N° Max",
      flex: 0.6,
      minWidth: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "durata",
      headerName: "Durata",
      flex: 0.6,
      minWidth: 90,
      align: "center",
      headerAlign: "center",
    },
    createActionsCol({
      onEdit: (row) => openModal("orario", "edit", row._raw),
      onCopy: (row) => openModal("orario", "copy", row._raw),
      onDelete: (row) => handleDelete("orario", row._raw),
      minWidth: 100,
    }),
  ];

  const prestazioniOrarioCols: GridColDef[] = [
    { field: "specialita", headerName: "Specialità", flex: 0.9, minWidth: 120 },
    {
      field: "prestazione",
      headerName: "Prestazione",
      flex: 1.2,
      minWidth: 160,
    },
    {
      field: "nMax",
      headerName: "N° Max",
      flex: 0.6,
      minWidth: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "durata",
      headerName: "Durata",
      flex: 0.6,
      minWidth: 90,
      align: "center",
      headerAlign: "center",
    },
    createActionsCol({
      onEdit: (row) => openModal("prestazione", "edit", row._raw),
      onDelete: (row) => handleDelete("prestazione", row._raw),
      minWidth: 100,
      hideCopy: true,
    }),
  ];

  const periodiValiditaCols: GridColDef[] = [
    {
      field: "dataInizio",
      headerName: "Data Inizio",
      flex: 0.9,
      minWidth: 120,
    },
    { field: "dataFine", headerName: "Data Fine", flex: 1.2, minWidth: 160 },
    createActionsCol({
      onEdit: (row) => openModal("periodo", "edit", row._raw),
      onDelete: (row) => handleDelete("periodo", row._raw),
      minWidth: 100,
      hideCopy: true,
    }),
  ];

  // Selezioni DataGrid
  const tipologiaSelection: GridSelectionModel = selectedTipologiaId
    ? [selectedTipologiaId]
    : [];
  const giornataSelection: GridSelectionModel = selectedGiornataId
    ? [selectedGiornataId]
    : [];
  const orarioSelection: GridSelectionModel = selectedOrarioId
    ? [selectedOrarioId]
    : [];

  return (
    <>
      <Helmet>
        <title>Dettaglio Programma</title>
      </Helmet>

      <AllertMessage
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={severity === "warning" ? confirmDelete : null}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      {modal.open && modal.entity === "tipologia" && (
        <TipologiesModal
          open
          onClose={closeModal}
          action={modal.action}
          initialData={modal.action === "insert" ? undefined : modal.payload}
          parentMethods={props.formMethods}
          createdIdPgmedt={createdIdPgmedt}
        />
      )}

      {modal.open && modal.entity === "orario" && (
        <OrariFissiModal
          open
          onClose={closeModal}
          action={modal.action}
          initialData={modal.action === "insert" ? undefined : modal.payload}
          parentMethods={props.formMethods}
          selectedGiornataId={selectedGiornataId}
          createdIdPgmedt={createdIdPgmedt}
        />
      )}

      {modal.open && modal.entity === "prestazione" && (
        <PrestazioniAbilitateModal
          open
          onClose={closeModal}
          action={modal.action}
          initialData={modal.action === "insert" ? undefined : modal.payload}
          parentMethods={props.formMethods}
          selectedOrarioId={selectedOrarioId}
          createdIdPgmedt={createdIdPgmedt}
        />
      )}

      {modal.open && modal.entity === "giornata" && (
        <GiornateModal
          open
          onClose={closeModal}
          action={modal.action}
          initialData={modal.action === "insert" ? undefined : modal.payload}
          parentMethods={props.formMethods}
          selectedTipologiaId={selectedTipologiaId}
          createdIdPgmedt={createdIdPgmedt}
        />
      )}

      {modal.open && modal.entity === "periodo" && (
        <PeriodiValiditaModal
          open
          onClose={closeModal}
          action={modal.action}
          initialData={modal.action === "insert" ? undefined : modal.payload}
          parentMethods={props.formMethods}
          createdIdPgmedt={createdIdPgmedt}
        />
      )}

      {modal.open && modal.entity === "sospensione" && (
        <SospensioniAgendeModal
          open
          onClose={closeModal}
          parentMethods={props.formMethods}
          createdIdPgmedt={createdIdPgmedt}
        />
      )}

      {modal.open && modal.entity === "attrGP" && (
        <AttrGPModal
          open
          onClose={closeModal}
          parentMethods={props.formMethods}
          onSavedPgmedtData={() => getPgmedtageData(watch_chiave, { force: true })}
        />
      )}

      <FormProvider {...props?.formMethods}>
        <Box sx={styles.page}>
          <Paper sx={styles.shell} elevation={0}>
            <Box
              component="form"
              onSubmit={props?.formMethods?.handleSubmit(onSubmit)}
            >
              <Box sx={styles.tabsWrap}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={sxTabs}>
                  <Tab value="dettaglio" label="Dettaglio" />
                  <Tab value="note" label="Note (Lun-Dom)" />
                  <Tab value="periodi" label="Periodi di validità" />
                </Tabs>
              </Box>

              {/* TAB Dettaglio */}
              {tab === "dettaglio" && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <RHFTextField
                        name="medIntNuovoProgramma.codProg"
                        control={props?.formMethods?.control}
                        label="Cod.Programma"
                        fullWidth
                        disabled={isCopyMode}
                        rules={{ required: "Campo obbligatorio" }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <RHFTextField
                        name="medIntNuovoProgramma.descrCompl"
                        control={props?.formMethods?.control}
                        label="Descrizione"
                        fullWidth
                        disabled={isCopyMode}
                        rules={{ required: "Campo obbligatorio" }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <RHFTextField
                        name="medIntNuovoProgramma.descrRid"
                        control={props?.formMethods?.control}
                        label="Descrizione ridotta"
                        fullWidth
                        disabled={isCopyMode}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFDateTimePicker
                        name="medIntNuovoProgramma.dataIni"
                        control={props?.formMethods?.control}
                        label="Inizio validità"
                        displayFormat="DD/MM/YY"
                        valueFormat="YYYY-MM-DD"
                        fullWidth
                        dateOnly
                        rules={{ required: "Campo obbligatorio" }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFDateTimePicker
                        name="medIntNuovoProgramma.dataFin"
                        control={props?.formMethods?.control}
                        label="Fine validità"
                        displayFormat="DD/MM/YY"
                        valueFormat="YYYY-MM-DD"
                        fullWidth
                        dateOnly
                        disabled={isCopyMode}
                        rules={{ required: "Campo obbligatorio" }}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <RHFCheckbox
                        name="medIntNuovoProgramma.appUltFascOra"
                        control={props?.formMethods?.control}
                        label="Appuntamento ultimo orario fasce"
                        disabled={isCopyMode}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFAutocomplete
                        name="medIntNuovoProgramma.disciplinaQD"
                        control={props?.formMethods?.control}
                        label="Disciplina (QD)"
                        fullWidth
                        options={[]}
                        disabled={isCopyMode}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <RHFAutocomplete
                        name="medIntNuovoProgramma.disciplinaAmm"
                        control={props?.formMethods?.control}
                        label="Disciplina (Amm)"
                        fullWidth
                        options={[]}
                        disabled={isCopyMode}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={4}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <RHFCheckbox
                        name="medIntNuovoProgramma.gpPerm"
                        control={props?.formMethods?.control}
                        label="GP-permetti altri orari"
                        disabled={isCopyMode}
                      />
                    </Grid>
                  </Grid>

                  {/* Tipologie */}
                  <Box sx={sxSection}>
                    <Box sx={sxSectionHeader}>
                      <Typography sx={sxSectionTitle}>Tipologie</Typography>
                      <Button
                        variant="outlinedCustom"
                        startIcon={<AddIcon />}
                        sx={{ textTransform: "none" }}
                        disabled={isCopyMode}
                        onClick={() => openModal("tipologia", "insert")}
                      >
                        Inserisci
                      </Button>
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <StyledDataGrid
                        rows={tipologieRows ?? []}
                        columns={tipologieCols}
                        getRowId={(r) => r.id}
                        selectionModel={tipologiaSelection}
                        onSelectionModelChange={(m) => {
                          const id = (m?.[0] as string) ?? null;
                          setSelectedTipologiaId(id);
                          const nextTipo =
                            (tipologie ?? []).find((t) => t.IDPGMEDD === id) ??
                            null;
                          const gId =
                            nextTipo?.orariGiornalieri?.[0]?.IDPGMEDG ?? null;
                          const oId =
                            nextTipo?.orariGiornalieri?.[0]?.orariFissi?.[0]
                              ?.IDPGMEDO ?? null;
                          setSelectedGiornataId(gId);
                          setSelectedOrarioId(oId);
                        }}
                        sortingOrder={["desc", "asc", null]}
                        sx={{
                          ...sxGridSelectedRow,
                          height: 260,
                          '& .MuiDataGrid-columnHeaders [data-field="__orderBySelected__"] .MuiDataGrid-sortIcon':
                            { display: "none" },
                        }}
                      />
                    </Box>
                  </Box>
                  {/* Giornate */}
                  <Box sx={sxSection}>
                    <Box sx={sxSectionHeader}>
                      <Typography sx={sxSectionTitle}>Giornate</Typography>
                      <Button
                        variant="outlinedCustom"
                        startIcon={<AddIcon />}
                        sx={{ textTransform: "none" }}
                        disabled={isCopyMode}
                        onClick={() =>
                          openModal("giornata", "insert", {
                            pgmeddId: selectedTipologiaId,
                          })
                        }
                      >
                        Inserisci
                      </Button>
                    </Box>

                    <Box sx={{ p: 2 }}>
                      <StyledDataGrid
                        rows={giornateRows ?? []}
                        columns={giornateCols}
                        getRowId={(r) => r.id}
                        selectionModel={giornataSelection}
                        onSelectionModelChange={(m) => {
                          const id = (m?.[0] as string) ?? null;
                          setSelectedGiornataId(id);

                          const nextG =
                            giornate.find((g: any) => g.IDPGMEDG === id) ??
                            null;
                          const oId = nextG?.orariFissi?.[0]?.IDPGMEDO ?? null;
                          setSelectedOrarioId(oId);
                        }}
                        sortingOrder={["desc", "asc", null]}
                        sx={{
                          ...sxGridSelectedRow,
                          height: 260,
                          '& .MuiDataGrid-columnHeaders [data-field="__orderBySelected__"] .MuiDataGrid-sortIcon':
                            { display: "none" },
                        }}
                      />
                    </Box>
                  </Box>
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {/* Orari fissi */}
                    <Grid item xs={12} md={6}>
                      <Box sx={sxCard}>
                        <Box sx={sxSectionHeader}>
                          <Typography sx={sxSectionTitle}>
                            Orari fissi nella giornata
                          </Typography>
                          <Button
                            variant="outlinedCustom"
                            sx={{
                              minWidth: 34,
                              width: 34,
                              height: 34,
                              p: 0,
                              borderRadius: 1,
                            }}
                            disabled={isCopyMode}
                            onClick={() => openModal("orario", "insert")}
                          >
                            <AddIcon />
                          </Button>
                        </Box>

                        <Box sx={{ p: 2 }}>
                          <StyledDataGrid
                            rows={orariFissiRows ?? []}
                            columns={orariFissiCols}
                            getRowId={(r) => r.id}
                            selectionModel={orarioSelection}
                            onSelectionModelChange={(m) => {
                              const id = (m?.[0] as string) ?? null;
                              setSelectedOrarioId(id);
                            }}
                            sortingOrder={["desc", "asc", null]}
                            sx={{
                              height: 260,
                              ...sxGridSelectedRow,
                              '& .MuiDataGrid-columnHeaders [data-field="__orderBySelected__"] .MuiDataGrid-sortIcon':
                                { display: "none" },
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                    {/* Prestazioni */}
                    <Grid item xs={12} md={6}>
                      <Box sx={sxCard}>
                        <Box sx={sxSectionHeader}>
                          <Typography sx={sxSectionTitle}>
                            Prestazioni abilitate nell'orario
                          </Typography>
                          <Button
                            variant="outlinedCustom"
                            sx={{
                              minWidth: 34,
                              width: 34,
                              height: 34,
                              p: 0,
                              borderRadius: 1,
                            }}
                            disabled={isCopyMode}
                            onClick={() => openModal("prestazione", "insert")}
                          >
                            <AddIcon />
                          </Button>
                        </Box>
                        <Box sx={{ p: 2 }}>
                          <StyledDataGrid
                            rows={prestazioniRows ?? []}
                            columns={prestazioniOrarioCols}
                            getRowId={(r) => r.id}
                            sortingOrder={["desc", "asc", null]}
                            sx={{
                              ...sxGridSelectedRow,
                              height: 260,
                              '& .MuiDataGrid-columnHeaders [data-field="__orderBySelected__"] .MuiDataGrid-sortIcon':
                                { display: "none" },
                            }}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* TAB Note */}
              {tab === "note" && (
                <Box>
                  <Box sx={sxNotesCard}>
                    <Grid container spacing={2}>
                      {[
                        { label: "Lunedì", key: "lunedi" as const },
                        { label: "Martedì", key: "martedi" as const },
                        { label: "Mercoledì", key: "mercoledi" as const },
                        { label: "Giovedì", key: "giovedi" as const },
                        { label: "Venerdì", key: "venerdi" as const },
                        { label: "Sabato", key: "sabato" as const },
                        { label: "Domenica", key: "domenica" as const },
                        { label: "Unica", key: "standard" as const },
                      ].map((f) => {
                        const fieldName =
                          `medIntNuovoProgramma.note.${f.key}` as const;
                        const value = props.formMethods.watch(fieldName) ?? "";
                        const len = String(value).length;

                        return (
                          <Grid item xs={12} key={f.key}>
                            <Box sx={sxNoteItem}>
                              <RHFTextField
                                name={fieldName}
                                control={props.formMethods.control}
                                label={f.label}
                                placeholder="Note eventuali"
                                fullWidth
                                multiline
                                rules={{
                                  maxLength: {
                                    value: 30,
                                    message: "Max 30 caratteri",
                                  },
                                }}
                                minRows={2}
                                maxRows={4}
                                inputProps={{ maxLength: 400 }}
                                disabled={isCopyMode}
                              />
                              <Typography sx={sxCounter}>
                                ({len}/400)
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </Box>
              )}
              {/* TAB Periodi */}
              {tab === "periodi" && (
                <Box sx={{ p: 2, color: "#666" }}>
                  <Grid container spacing={2}>
                    <Grid
                      item
                      xs={12}
                      md={12}
                      display="flex"
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="outlinedCustom"
                        startIcon={<AddIcon />}
                        sx={{ textTransform: "none" }}
                        disabled={isCopyMode}
                        onClick={() => openModal("periodo", "insert")}
                      >
                        Inserisci
                      </Button>
                    </Grid>

                    <Grid item xs={12} md={12}>
                      <StyledDataGrid
                        rows={periodiValiditaRows}
                        columns={periodiValiditaCols}
                        getRowId={(r) => r.id}
                        sortingOrder={["desc", "asc", null]}
                        sx={{
                          ...sxGridSelectedRow,
                          height: 260,
                          '& .MuiDataGrid-columnHeaders [data-field="__orderBySelected__"] .MuiDataGrid-sortIcon':
                            { display: "none" },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Box sx={styles.actionsBar}>
                <Box sx={{ justifySelf: "start" }}>
                  <Button
                    variant="outlinedError"
                    sx={{ textTransform: "none" }}
                    onClick={handleClose}
                  >
                    Uscita
                  </Button>
                </Box>
                <Stack
                  direction="row"
                  spacing={3}
                  sx={{ justifySelf: "center" }}
                >
                  <Button
                    variant="outlinedCustom"
                    sx={{ textTransform: "none" }}
                    onClick={() => openModal("sospensione")}
                  >
                    Sospensione
                  </Button>
                  <Button
                    variant="outlinedCustom"
                    sx={{ textTransform: "none" }}
                    onClick={() => openModal("attrGP")}
                  >
                    Attr. GP
                  </Button>
                </Stack>
                <Box sx={{ justifySelf: "end" }}>
                  <Button
                    type="submit"
                    variant="containedCustom"
                    sx={{ textTransform: "none" }}
                  >
                    Conferma
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Box>
      </FormProvider>
    </>
  );
};

export default MedintAgendaFormDetail;
